import { observer } from "mobx-react-lite";
import { FC, useState, useCallback } from "react";
import styled from "@emotion/styled";
import { DebugModeTapDetector } from "@/components/layout/components/account-profile/debug-mode-tap-detector";
import { AccountInfo } from "@/components/layout/components/account-info/account-info";
import { OAuthCardWrapper } from "@/pages/oauth-consent/oauth-card-wrapper";
import { MDSButton, MDSButtonVariant, MDSLogo, MDSLogoKind } from "@/mds";

enum CardState {
  Initial = "initial",
  Approved = "approved",
  Denied = "denied",
  Error = "error",
}

export interface OAuthCardProps {
  appName: string;
  /**
   * Not currently used.
   * In the future, we'll show the "real" scopes from the backend.
   */
  scopeStr: string;
  accountInfo: {
    profilePhotoUrl: string | null;
    profileDisplayName: string;
    profileEmailAddress: string;
  };
  handleApprove: () => Promise<void>;
  handleDeny: () => Promise<void>;
}

export const OAuthCard: FC<OAuthCardProps> = observer(
  ({ appName, accountInfo, handleApprove, handleDeny }) => {
    const [isApproveLoading, setIsApproveLoading] = useState(false);
    const [isDenyLoading, setIsDenyLoading] = useState(false);
    const [cardState, setCardState] = useState<CardState>(CardState.Initial);

    const onClickApprove = useCallback(async () => {
      setIsApproveLoading(true);
      try {
        await handleApprove();
        setCardState(CardState.Approved);
      } catch (_unknownErr) {
        setCardState(CardState.Error);
      } finally {
        setIsApproveLoading(false);
      }
    }, [handleApprove]);

    const onClickDeny = useCallback(async () => {
      setIsDenyLoading(true);
      try {
        await handleDeny();
        setCardState(CardState.Denied);
      } catch (_unknownErr) {
        setCardState(CardState.Error);
      } finally {
        setIsDenyLoading(false);
      }
    }, [handleDeny]);

    if (cardState === CardState.Approved) {
      return (
        <OAuthCardWrapper>
          <CardSection>
            <TitleSection>Access Approved</TitleSection>
            <MessageSection>
              You have successfully granted access to {appName}. You may now close this window.
            </MessageSection>
          </CardSection>
        </OAuthCardWrapper>
      );
    }

    if (cardState === CardState.Denied) {
      return (
        <OAuthCardWrapper>
          <CardSection>
            <TitleSection>Canceled</TitleSection>
            <MessageSection>You may now close this window.</MessageSection>
          </CardSection>
        </OAuthCardWrapper>
      );
    }

    if (cardState === CardState.Error) {
      return (
        <OAuthCardWrapper>
          <CardSection>
            <TitleSection>Something went wrong</TitleSection>
            <MessageSection>
              An error occurred while processing your request. Please refresh the page and try
              again.
            </MessageSection>
          </CardSection>
        </OAuthCardWrapper>
      );
    }

    return (
      <OAuthCardWrapper>
        <CardSection>
          <TitleSection>{appName} wants permission to access your Mem Account</TitleSection>

          <DebugModeTapDetector>
            <StyledLogo kind={MDSLogoKind.Arrows} />
          </DebugModeTapDetector>

          <AccountSection>
            <AccountInfo
              profilePhotoUrl={accountInfo.profilePhotoUrl}
              profileDisplayName={accountInfo.profileDisplayName}
              profileEmailAddress={accountInfo.profileEmailAddress}
            />
          </AccountSection>

          <ScopeSection>
            <ScopeMessage>This will allow {appName} to:</ScopeMessage>
            <ScopeItem>• Access your notes</ScopeItem>
            <ScopeItem>• Access your collections</ScopeItem>
          </ScopeSection>

          <PolicySection>
            By clicking Allow Access, you allow this app to use your information in accordance to
            their terms of service and privacy policies. You can change this at any time in the Mem
            application.
          </PolicySection>
        </CardSection>

        <ActionSection>
          <MDSButton
            variant={MDSButtonVariant.Transparent}
            onClick={onClickDeny}
            isDisabled={isApproveLoading}
            isLoading={isDenyLoading}
          >
            Cancel
          </MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            onClick={onClickApprove}
            isDisabled={isDenyLoading}
            isLoading={isApproveLoading}
          >
            Allow Access
          </MDSButton>
        </ActionSection>
      </OAuthCardWrapper>
    );
  }
);

const AccountSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 8px;
`;

const TitleSection = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes["header-m"]};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: ${({ theme }) => theme.lineSpacings["header-l"]};
  margin-bottom: 28px;
  text-align: center;
`;

const ScopeSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
  margin-top: 32px;
  margin-bottom: 32px;
`;

const ScopeMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ScopeItem = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PolicySection = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MessageSection = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  margin-top: 16px;
`;

const CardSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 24px;
  justify-content: space-between;
  width: 100%;
`;

const StyledLogo = styled(MDSLogo)`
  height: 56px;
  width: 56px;
`;
